import { BottomSheet } from "@/design-system/BottomSheet/BottomSheet";
import { Foldout } from "@/design-system/Foldout/Foldout";
import { cn } from "@/lib/utils";
import { EA_WEB_APP_URL } from "@/settings";
import type { GalleryLevelPlan, GalleryLevelPlanSet } from "@repo/shared/api";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { Copy } from "lucide-react";
import { ReactNode, useState } from "react";
import { NavLink } from "react-router";

import { StepNumber } from "./GalleryFirstOwnerSheet";

const CHEAPER = [
  "cheaperPacks",
  "cheaperMarquee",
  "cheaperUpgrades",
  "cheaperSilver",
  "cheaperRebuild",
] as const;

const webAppLink = (
  <a
    href={EA_WEB_APP_URL}
    target="_blank"
    rel="noreferrer"
    className="text-blue-400 underline"
  />
);

const firstOwnersPage = (
  <NavLink
    to="/fut-gallery/update-first-owners"
    className="text-blue-400 underline"
  />
);

/** One of a step's lettered points: (a), (b), (c). */
const Lettered = ({
  letter,
  children,
}: {
  letter: string;
  children: ReactNode;
}) => (
  <li className="flex gap-x-1.5">
    <span className="shrink-0 tabular-nums text-foreground-muted">
      ({letter})
    </span>
    <span>{children}</span>
  </li>
);

/** A plan group's step title, like the page's, with its done count once planned. */
const GroupTitle = ({
  group,
  plan,
}: {
  group: GalleryLevelPlanSet["group"];
  plan?: GalleryLevelPlan;
}) => {
  const { t } = useTranslation("gallery");
  const sets = plan?.sets.filter((set) => set.group === group) ?? [];
  if (!sets.length) return <span>{t(`levels.groups.${group}`)}</span>;
  return (
    <span className="flex flex-wrap items-center gap-x-2">
      <span>{t(`levels.groups.${group}`)}</span>
      <span className="tabular-nums text-foreground-muted">
        {t("levels.groups.done", {
          done: sets.filter((set) => set.done).length,
          total: sets.length,
        })}
      </span>
    </span>
  );
};

/** The guide's steps, numbered like the page's. */
const GuideSteps = ({
  plan,
  className,
}: {
  plan?: GalleryLevelPlan;
  className?: string;
}) => {
  const { t } = useTranslation("gallery");
  return (
    <ol className={cn("flex flex-col gap-y-5 text-sm", className)}>
      <li className="flex items-start gap-x-2">
        <StepNumber n={1} />
        <div className="flex flex-col gap-y-1">
          <span>{t("steps.import.title")}</span>
          <span className="text-foreground-muted">
            {t("levels.guide.importText")}
          </span>
        </div>
      </li>
      <li className="flex items-start gap-x-2">
        <StepNumber n={2} />
        <div className="flex flex-col items-start gap-y-1">
          <span>{t("steps.sync.title")}</span>
          <span className="text-foreground-muted">
            {t("levels.guide.syncText")}
          </span>
          <NavLink
            to="/fut-gallery/update-first-owners"
            className="text-blue-400 underline"
          >
            {t("firstOwners.open")}
          </NavLink>
        </div>
      </li>
      <li className="flex items-start gap-x-2">
        <StepNumber n={3} />
        <div className="flex flex-col gap-y-2.5">
          <div className="flex flex-col gap-y-1">
            <span>{t("levels.steps.plan.title")}</span>
            <ol className="flex flex-col gap-y-0.5 text-foreground-muted">
              <Lettered letter="a">{t("levels.guide.buildLevel")}</Lettered>
              <Lettered letter="b">{t("levels.guide.buildLimits")}</Lettered>
              <Lettered letter="c">{t("levels.guide.buildClick")}</Lettered>
            </ol>
          </div>
          <div className="flex flex-col gap-y-1">
            <span>{t("levels.guide.worth")}</span>
            <span className="text-foreground-muted">
              {t("levels.guide.worthText")}
            </span>
            <span className="text-foreground-muted">
              {t("levels.guide.cheaper")}
            </span>
            <ul className="flex list-disc flex-col gap-y-0.5 pl-5 text-foreground-muted">
              {CHEAPER.map((key) => (
                <li key={key}>{t(`levels.guide.${key}`)}</li>
              ))}
            </ul>
          </div>
        </div>
      </li>
      <li className="flex items-start gap-x-2">
        <StepNumber n={4} />
        <div className="flex flex-col gap-y-1">
          <GroupTitle group="improve" plan={plan} />
          <span className="text-foreground-muted">
            {t("levels.guide.improveIntro")}
          </span>
          <ol className="flex flex-col gap-y-0.5 text-foreground-muted">
            <Lettered letter="a">{t("levels.guide.improveSame")}</Lettered>
            <Lettered letter="b">{t("levels.guide.improveLower")}</Lettered>
            <Lettered letter="c">
              <Trans
                ns="gallery"
                i18nKey="levels.guide.improveHigher"
                components={{ page: firstOwnersPage }}
              />
            </Lettered>
          </ol>
        </div>
      </li>
      <li className="flex items-start gap-x-2">
        <StepNumber n={5} />
        <div className="flex flex-col gap-y-1">
          <GroupTitle group="buy" plan={plan} />
          <span className="text-foreground-muted">
            {t("levels.guide.buyText")}
          </span>
          <ul className="flex list-disc flex-col gap-y-0.5 pl-5 text-foreground-muted">
            {/* The set page's buy and sell steps. */}
            <li>
              <Trans
                ns="gallery"
                i18nKey="guide.step2a"
                components={{
                  icon: <Copy className="inline size-3.5 align-[-2px]" />,
                }}
              />
            </li>
            <li>
              <Trans
                ns="gallery"
                i18nKey="guide.step2b"
                components={{ app: webAppLink }}
              />
            </li>
            <li>{t("guide.step2c")}</li>
            <li>{t("guide.step2d")}</li>
            <li>
              <Trans
                ns="gallery"
                i18nKey="levels.guide.buyComplete"
                components={{ app: webAppLink }}
              />
            </li>
            <li>{t("levels.guide.buyMarkDone")}</li>
          </ul>
        </div>
      </li>
    </ol>
  );
};

/** Before the first plan: the guide inline between the steps and the planner, folded until opened. */
export const GalleryLevelsGuideCard = ({
  plan,
}: {
  plan?: GalleryLevelPlan;
}) => {
  const { t } = useTranslation("gallery");
  const [open, setOpen] = useState(false);
  return (
    // Phones: edge to edge like the planner under it.
    <div className="-mx-4 border-y border-neutral-700 bg-neutral-800 p-4 mobile:mx-0 mobile:rounded-2xl mobile:border">
      <Foldout
        label={t("levels.guide.title")}
        open={open}
        onToggle={() => setOpen(!open)}
      >
        <GuideSteps plan={plan} className="pt-4" />
      </Foldout>
    </div>
  );
};

/** Once a plan exists: the guide in a bottom sheet, from the page header's link. */
export const GalleryLevelsGuideSheet = ({
  plan,
  isOpen,
  onClose,
}: {
  plan?: GalleryLevelPlan;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation("gallery");
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      className="max-h-[85dvh] max-w-full bg-neutral-900"
      header={{
        title: t("levels.guide.title"),
        closeLabel: t("firstOwners.close"),
      }}
    >
      <GuideSteps
        plan={plan}
        className="mx-auto w-full max-w-3xl px-4 pb-8 pt-2"
      />
    </BottomSheet>
  );
};
